/**
 * V2-only onboarding CRUD actions.
 *
 * This hook is used by active V2 onboarding surfaces and intentionally avoids
 * legacy V1 ScreenGraph clone/delete paths.
 */

import { useState, useCallback, useRef } from 'react';
import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import { versionAnimationDefaultsFromSettings } from '@modules/themes/animation/applyAnimationSettingsToFlowDocument';
import { CreateOnboardingInputV2 } from '../types/onboardings.types';

export interface UseOnboardingActionsV2Options {
  projectId: string;
  userId: string;
}

export interface UseOnboardingActionsV2Result {
  createOnboarding: (input: CreateOnboardingInputV2) => Promise<string>;
  cloneOnboarding: (onboardingId: string, newName: string) => Promise<string>;
  deleteOnboarding: (onboardingId: string) => Promise<void>;
  moveToFolder: (onboardingId: string, folderId: string | null) => Promise<void>;
  loading: boolean;
  error: string | null;
}

export function useOnboardingActionsV2(options: UseOnboardingActionsV2Options): UseOnboardingActionsV2Result {
  const { projectId, userId } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onboardingServiceRef = useRef(new OnboardingService());

  const createOnboarding = useCallback(async (input: CreateOnboardingInputV2): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const onboardingService = onboardingServiceRef.current;
      const onboarding = await onboardingService.createOnboarding(projectId, {
        name: input.name,
        folderId: input.folderId ?? null,
        sourceThemeId: input.themeSettingsV2?.sourceThemeId ?? null,
        defaultLanguage: input.defaultLanguage,
        languages: input.languages,
        platform: input.platform,
        screenGraphType: 'v2',
      });

      if (input.themeSettingsV2) {
        await onboardingService.saveThemeSettingsV2(
          projectId,
          onboarding.id,
          input.themeSettingsV2
        );
      }

      // Theme motion: screen ENTRANCES ride each template screen's content,
      // but the screen-to-screen transition lives on version-level
      // screensGraph defaults — without writing them here, a funnel created
      // from an animated theme never transitions until someone manually runs
      // Apply→Animations. "None"/absent settings produce no fields (omit,
      // never null, on a fresh doc).
      // TODO(strategy): strategy-generated funnels bypass this hook — inject
      // the same defaults where the strategy materializes screensGraph
      // (builder-service CliStrategyTransformService).
      const animationDefaults = versionAnimationDefaultsFromSettings(
        input.themeSettingsV2?.animationSettings
      );

      await onboardingService.createVersion(
        projectId,
        onboarding.id,
        {
          defaultLanguage: input.defaultLanguage,
          languages: input.languages,
          schemaVersion: '2.0.0',
          screens: {},
          launchScreenId: '',
          metadata: {},
          ...(animationDefaults ?? {}),
        },
        'Draft',
        userId
      );

      return onboarding.id;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create V2 onboarding';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectId, userId]);

  const cloneOnboarding = useCallback(async (onboardingId: string, newName: string): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const onboardingService = onboardingServiceRef.current;
      const clonedOnboarding = await onboardingService.cloneOnboarding(
        projectId,
        onboardingId,
        newName,
        userId
      );

      return clonedOnboarding.id;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to clone V2 onboarding';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectId, userId]);

  const deleteOnboarding = useCallback(async (onboardingId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const onboardingService = onboardingServiceRef.current;
      await onboardingService.deleteOnboarding(projectId, onboardingId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete V2 onboarding';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const moveToFolder = useCallback(async (onboardingId: string, folderId: string | null): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const onboardingService = onboardingServiceRef.current;
      await onboardingService.moveToFolder(projectId, onboardingId, folderId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to move V2 onboarding';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  return {
    createOnboarding,
    cloneOnboarding,
    deleteOnboarding,
    moveToFolder,
    loading,
    error,
  };
}
